import type { Optional } from '@festina-life/core'
import type { ParserRuleContext } from 'antlr4'

import type {
  FunctionDefinitionContext,
  FunctionScriptContext,
  FunctionValueParameterContext,
  FunctionValueParametersContext,
  ReceiverTypeContext,
  SimpleIdentifierContext,
} from '../lexer/ANTLR/FPlusParser'
import FPlusParserVisitor from '../lexer/ANTLR/FPlusParserVisitor'

export type FunctionMetadata = {
  name: string
  module?: string
  parameters: ParameterMetadata[]
  returnType: string
  // If the return type is a generic wrapper like List<String>, these fields
  // expose the outer wrapper and the inner type(s)
  wrapperType?: Optional<string>
  internalReturnType: string
  body: Optional<string>
}

export type ParameterMetadata = {
  name: string
  type: Optional<string>
  hasDefaultValue: boolean
  defaultValue?: Optional<string>
}

export class FunctionExtractor extends FPlusParserVisitor<FunctionMetadata[]> {
  private functions: FunctionMetadata[] = []
  private originalInput = ''

  // Set the original input string for formatting preservation
  setOriginalInput(input: string): void {
    this.originalInput = input
  }

  override visitFunctionScript = (
    ctx: FunctionScriptContext,
  ): FunctionMetadata[] => {
    const functionDef = ctx.functionDefinition()
    if (functionDef) {
      // Validate that this is actually a function definition, not just a property assignment
      if (this.isValidFunctionDefinition(functionDef)) {
        const metadata = this.extractFunctionMetadata(functionDef, ctx)
        if (metadata) {
          this.functions.push(metadata)
        }
      }
    }
    return this.functions
  }

  private isValidFunctionDefinition(ctx: FunctionDefinitionContext): boolean {
    // A valid function definition must have:
    // 1. A simple identifier (function name)
    // 2. Function value parameters (parentheses)
    // 3. A return type (colon followed by type)

    const hasName = !!ctx.simpleIdentifier()
    const hasParameters = !!ctx.functionValueParameters()
    const hasReturnType = !!ctx.type_()

    // All three are required for a valid function definition
    return hasName && hasParameters && hasReturnType
  }

  private extractFunctionMetadata(
    ctx: FunctionDefinitionContext,
    scriptCtx: FunctionScriptContext,
  ): FunctionMetadata | null {
    // Extract function name
    const nameNode = ctx.simpleIdentifier()
    const name = nameNode ? this.extractIdentifier(nameNode) : ''

    // Extract module name (if present)
    const module = this.extractModuleName(ctx)

    // Extract parameters
    const paramsNode = ctx.functionValueParameters()
    const parameters = this.extractParameters(paramsNode)

    // Extract return type
    const returnTypeNode = ctx.type_()
    const returnType = returnTypeNode
      ? this.extractTextWithFormatting(returnTypeNode)
      : undefined

    const { wrapperType, internalReturnType } =
      this.parseWrapperReturnType(returnType)

    // Extract function body with preserved formatting
    const bodyNode = scriptCtx.expressionScript()
    const body = bodyNode ? this.extractTextWithFormatting(bodyNode) : undefined

    return name && returnType && internalReturnType
      ? {
          name,
          module,
          parameters,
          returnType,
          wrapperType,
          internalReturnType,
          body,
        }
      : null
  }

  private parseWrapperReturnType(returnType: Optional<string>): {
    wrapperType?: string
    internalReturnType?: string
  } {
    if (!returnType) {
      return {}
    }

    const trimmed = returnType.trim()

    // Quickly reject obvious non-generic types
    const firstLessThanIndex = trimmed.indexOf('<')
    if (firstLessThanIndex === -1) {
      // No wrapper; internal equals returnType
      return { internalReturnType: trimmed }
    }

    // Find the matching '>' for the first '<' taking into account nesting
    let depth = 0
    let matchIndex = -1
    for (let i = firstLessThanIndex; i < trimmed.length; i++) {
      const currentChar = trimmed[i]
      if (currentChar === '<') {
        depth += 1
      } else if (currentChar === '>') {
        depth -= 1
        if (depth === 0) {
          matchIndex = i
          break
        }
      }
    }

    if (matchIndex === -1) {
      // Malformed generic; treat as non-wrapper
      return { internalReturnType: trimmed }
    }

    const wrapper = trimmed.substring(0, firstLessThanIndex).trim()
    const inner = trimmed.substring(firstLessThanIndex + 1, matchIndex).trim()

    if (!wrapper || !inner) {
      // Something like <T> without a wrapper name – treat as non-wrapper
      return { internalReturnType: trimmed }
    }

    return {
      wrapperType: wrapper.replace(/\s+/g, ' '),
      internalReturnType: inner.replace(/\s+/g, ' '),
    }
  }

  private extractModuleName(
    ctx: FunctionDefinitionContext,
  ): string | undefined {
    try {
      // Check if there's a receiver type (module prefix)
      const receiverType = ctx.receiverType()
      if (receiverType) {
        // Extract the module name from the receiver type
        return this.extractReceiverTypeText(receiverType)
      }
      return undefined
    } catch {
      return undefined
    }
  }

  private extractReceiverTypeText(ctx: ReceiverTypeContext): string {
    try {
      // The receiver type could be a userType, parenthesizedType, etc.
      // For now, we'll extract the text and clean it up
      const text = ctx.getText()
      // Remove any parentheses if it's a parenthesized type
      return text.replace(/^\(|\)$/g, '')
    } catch {
      return ''
    }
  }

  private extractTextWithFormatting(
    ctx: Optional<ParserRuleContext>,
  ): Optional<string> {
    if (!ctx) {
      return
    }

    // Get the start and stop token positions
    const startToken = ctx.start
    const stopToken = ctx.stop

    if (startToken && stopToken && this.originalInput) {
      // Extract the text from the original input using token positions
      const startIndex = startToken.start
      const stopIndex = stopToken.stop
      if (startIndex !== undefined && stopIndex !== undefined) {
        return this.originalInput.substring(startIndex, stopIndex + 1)
      }
    }

    // Fallback to getText() if we can't get the original input
    return ctx.getText()
  }

  private extractParameters(
    ctx: FunctionValueParametersContext | undefined,
  ): ParameterMetadata[] {
    if (!ctx) {
      return []
    }

    const parameters: ParameterMetadata[] = []
    const paramList = ctx.functionValueParameter_list()

    for (const param of paramList) {
      const paramMetadata = this.extractParameter(param)
      if (paramMetadata) {
        parameters.push(paramMetadata)
      }
    }

    return parameters
  }

  private extractParameter(
    ctx: FunctionValueParameterContext,
  ): ParameterMetadata | null {
    const paramNode = ctx.parameter()
    if (!paramNode) {
      return null
    }

    const name = this.extractIdentifier(paramNode.simpleIdentifier())
    const type = this.extractTextWithFormatting(paramNode.type_())
    const hasDefaultValue = !!ctx.ASSIGNMENT()

    const defaultValue =
      hasDefaultValue && ctx.expression()
        ? this.extractTextWithFormatting(ctx.expression())
        : undefined

    return {
      name,
      type,
      hasDefaultValue,
      defaultValue,
    }
  }

  private extractIdentifier(ctx: SimpleIdentifierContext | undefined): string {
    if (!ctx) {
      return ''
    }

    // Check for different identifier types
    if (ctx.Identifier()) {
      return ctx.Identifier()!.getText()
    }
    if (ctx.WHERE()) {
      return ctx.WHERE()!.getText()
    }
    if (ctx.MAP()) {
      return ctx.MAP()!.getText()
    }
    if (ctx.ASSERT()) {
      return ctx.ASSERT()!.getText()
    }
    if (ctx.IS_IN()) {
      return ctx.IS_IN()!.getText()
    }
    if (ctx.NOT_IN()) {
      return ctx.NOT_IN()!.getText()
    }

    return ''
  }
}
